import { FloatingNode, useFloatingParentNodeId, FloatingTree } from '@floating-ui/react';
import * as React from 'react';
import { ModalContent } from './modal_content.js';
import { ModalContext } from './modal_context.js';
import { ModalTrigger } from './modal_trigger.js';
import { useModal } from './use_modal.js';
import '../../utils/index.js';
import { useCachedProp } from '../../utils/cached_prop.js';

const isTriggerHTMLElement = (trigger) => {
    return !(React.isValidElement(trigger) || typeof trigger === 'string' || trigger === null);
};
const ModalInner = ({ children: propChildren, trigger, open, onToggle, size = 'small', className: propClassName, loading, closeOnOutsidePress = true, closeListener, outsidePress, ...rest }) => {
    const referenceElement = isTriggerHTMLElement(trigger) ? trigger : null;
    const triggerChild = !isTriggerHTMLElement(trigger) ? trigger : null;
    const state = useModal({
        open,
        onToggle,
        loading,
        closeOnOutsidePress,
        outsidePress,
        size,
        referenceElement,
        closeListener,
    });
    // Cache children to a ref when the Modal is open.
    // The cached children are rendered when open is false, i.e. during the out transition.
    // This is necessary, as usually the open state is controlled from outside,
    // and the children are removed before the out transition has completed.
    const children = useCachedProp({ prop: propChildren, shouldUpdate: state.open });
    const className = useCachedProp({ prop: propClassName, shouldUpdate: state.open });
    return (React.createElement(FloatingNode, { id: state.nodeId },
        React.createElement(ModalContext.Provider, { value: state },
            triggerChild && React.createElement(ModalTrigger, null, triggerChild),
            React.createElement(ModalContent, { ...rest, className: className }, children))));
};
/**
 * A dialog that appears over the rest of the content.
 */
const Modal = (props) => {
    const parentId = useFloatingParentNodeId();
    // This is a root, so we wrap it with the tree
    if (parentId === null) {
        return (React.createElement(FloatingTree, null,
            React.createElement(ModalInner, { ...props })));
    }
    return React.createElement(ModalInner, { ...props });
};

export { Modal };
